import { useCallback, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  useImportCampaign,
  useImportPreview,
  useImportGroups,
  useUploadImportFile,
  useProcessCampaign,
  useCreateEntities,
  useCancelCampaign,
  useConfirmMatch,
  type CampaignStatus,
} from "@/api/hooks";

const STATUS_LABELS: Record<CampaignStatus, string> = {
  pending: "En attente du fichier",
  analyzing: "Analyse en cours...",
  analyzed: "Fichier analysé",
  matching: "Matching catalogue en cours...",
  enriching: "Enrichissement BnF en cours...",
  paused: "En pause - validation requise",
  creating: "Création des entités...",
  completed: "Import terminé",
  failed: "Échec de l'import",
  cancelled: "Annulé",
};

const CONFIDENCE_LABELS: Record<string, string> = {
  exact: "Exact",
  high: "Élevé",
  medium: "Moyen",
  low: "Faible",
  none: "Aucun",
};

const CONFIDENCE_COLORS: Record<string, string> = {
  exact: "text-green-600 dark:text-green-400",
  high: "text-green-500 dark:text-green-400",
  medium: "text-yellow-600 dark:text-yellow-400",
  low: "text-orange-600 dark:text-orange-400",
  none: "text-gray-500 dark:text-gray-400",
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes.toString() + " o";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " Ko";
  return (bytes / (1024 * 1024)).toFixed(1) + " Mo";
}

export function ImportCampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<"overview" | "preview" | "groups">("overview");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { data, isLoading, error, refetch } = useImportCampaign(id);
  const { data: previewData } = useImportPreview(id, { limit: 20 });
  const { data: groupsData } = useImportGroups(id, { limit: 20, needsReview: true });

  const uploadFile = useUploadImportFile();
  const processCampaign = useProcessCampaign();
  const createEntities = useCreateEntities();
  const cancelCampaign = useCancelCampaign();
  const confirmMatch = useConfirmMatch();

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!id || !selectedFile) return;
    try {
      await uploadFile.mutateAsync({ campaignId: id, file: selectedFile });
      setSelectedFile(null);
      void refetch();
    } catch {
      // Error handled by mutation
    }
  }, [id, selectedFile, uploadFile, refetch]);

  const handleProcess = useCallback(async () => {
    if (!id) return;
    await processCampaign.mutateAsync(id);
  }, [id, processCampaign]);

  const handleCreate = useCallback(async () => {
    if (!id) return;
    await createEntities.mutateAsync(id);
  }, [id, createEntities]);

  const handleCancel = useCallback(async () => {
    if (!id || !confirm("Voulez-vous vraiment annuler cette campagne ?")) return;
    await cancelCampaign.mutateAsync(id);
  }, [id, cancelCampaign]);

  const handleConfirmMatch = useCallback(
    async (groupId: string, confirmed: boolean) => {
      if (!id) return;
      await confirmMatch.mutateAsync({ campaignId: id, groupId, confirmed });
    },
    [id, confirmMatch]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-400">
        Campagne non trouvée
        <Link to="/import" className="ml-4 underline">
          Retour
        </Link>
      </div>
    );
  }

  const { campaign, progress } = data;
  const isProcessing = ["analyzing", "matching", "enriching", "creating"].includes(campaign.status);
  const needsReview = groupsData && groupsData.groups.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Link
              to="/import"
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </Link>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {campaign.name}
            </h1>
          </div>
          {campaign.description && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{campaign.description}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          {campaign.status === "analyzed" && (
            <button
              onClick={handleProcess}
              disabled={processCampaign.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {processCampaign.isPending ? "Lancement..." : "Lancer le traitement"}
            </button>
          )}

          {campaign.status === "paused" && !needsReview && (
            <button
              onClick={handleCreate}
              disabled={createEntities.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              {createEntities.isPending ? "Création..." : "Créer les entités"}
            </button>
          )}

          {!["completed", "cancelled", "failed"].includes(campaign.status) && (
            <button
              onClick={handleCancel}
              disabled={cancelCampaign.isPending}
              className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              Annuler
            </button>
          )}
        </div>
      </div>

      {/* Status Banner */}
      <div
        className={`rounded-lg p-4 ${
          campaign.status === "failed"
            ? "bg-red-50 dark:bg-red-900/20"
            : campaign.status === "completed"
              ? "bg-green-50 dark:bg-green-900/20"
              : campaign.status === "paused"
                ? "bg-yellow-50 dark:bg-yellow-900/20"
                : "bg-blue-50 dark:bg-blue-900/20"
        }`}
      >
        <div className="flex items-center gap-3">
          {isProcessing && (
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
          )}
          <span
            className={`font-medium ${
              campaign.status === "failed"
                ? "text-red-700 dark:text-red-400"
                : campaign.status === "completed"
                  ? "text-green-700 dark:text-green-400"
                  : campaign.status === "paused"
                    ? "text-yellow-700 dark:text-yellow-400"
                    : "text-blue-700 dark:text-blue-400"
            }`}
          >
            {STATUS_LABELS[campaign.status]}
          </span>
          {isProcessing && (
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {Math.round(progress.progress)}% ({progress.processedRows} / {progress.totalRows}{" "}
              lignes)
            </span>
          )}
        </div>

        {/* Progress Bar */}
        {isProcessing && (
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full rounded-full bg-blue-600 transition-all duration-500"
              style={{ width: progress.progress.toString() + "%" }}
            />
          </div>
        )}
      </div>

      {/* Upload Section (only if pending) */}
      {campaign.status === "pending" && (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center dark:border-gray-600">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          <h3 className="mt-4 text-sm font-medium text-gray-900 dark:text-white">
            {selectedFile ? selectedFile.name : "Sélectionnez un fichier CSV"}
          </h3>
          {selectedFile && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {formatFileSize(selectedFile.size)}
            </p>
          )}
          <div className="mt-4 flex justify-center gap-3">
            <label className="cursor-pointer rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">
              Parcourir
              <input
                type="file"
                accept=".csv,.txt"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
            {selectedFile && (
              <button
                onClick={handleUpload}
                disabled={uploadFile.isPending}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {uploadFile.isPending ? "Upload..." : "Uploader"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      {campaign.status !== "pending" && (
        <>
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex gap-6">
              {(["overview", "preview", "groups"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                  }}
                  className={`border-b-2 px-1 py-3 text-sm font-medium ${
                    activeTab === tab
                      ? "border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                      : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  }`}
                >
                  {tab === "overview" && "Vue d'ensemble"}
                  {tab === "preview" && `Aperçu (${String(campaign.counts.validRows)})`}
                  {tab === "groups" && (
                    <>
                      Groupes ({String(campaign.counts.biblioGroups)})
                      {needsReview && (
                        <span className="ml-2 rounded-full bg-yellow-100 px-2 py-0.5 text-xs text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">
                          {groupsData.groups.length} à valider
                        </span>
                      )}
                    </>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <StatCard label="Lignes totales" value={campaign.counts.totalRows} />
              <StatCard label="Lignes valides" value={campaign.counts.validRows} color="green" />
              <StatCard label="Lignes invalides" value={campaign.counts.invalidRows} color="red" />
              <StatCard label="Groupes biblio" value={campaign.counts.biblioGroups} color="blue" />
              <StatCard
                label="Notices créées"
                value={campaign.counts.createdRecords}
                color="green"
              />
              <StatCard
                label="Exemplaires créés"
                value={campaign.counts.createdItems}
                color="green"
              />
              <StatCard label="Lignes matchées" value={progress.matchedRows} color="cyan" />
              <StatCard label="Lignes enrichies" value={progress.enrichedRows} color="purple" />
            </div>
          )}

          {/* Preview Tab */}
          {activeTab === "preview" && previewData && (
            <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Notice
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Titre
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Auteur
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Code-barres
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Statut
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                  {previewData.rows.map((row) => (
                    <tr key={row.id}>
                      <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-500">
                        {row.lineNumber}
                      </td>
                      <td className="px-4 py-3 font-mono text-sm whitespace-nowrap text-gray-900 dark:text-white">
                        {row.legacyNoticeId || "—"}
                      </td>
                      <td className="max-w-[300px] truncate px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {row.title || "—"}
                      </td>
                      <td className="max-w-[200px] truncate px-4 py-3 text-sm text-gray-500">
                        {row.author || "—"}
                      </td>
                      <td className="px-4 py-3 font-mono text-sm whitespace-nowrap text-gray-500">
                        {row.barcode || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            row.status === "valid"
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                              : row.status === "invalid"
                                ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                                : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Groups Tab */}
          {activeTab === "groups" && groupsData && (
            <div className="space-y-4">
              {groupsData.groups.length === 0 ? (
                <div className="rounded-lg bg-green-50 p-4 text-green-700 dark:bg-green-900/20 dark:text-green-400">
                  Tous les groupes ont été validés
                </div>
              ) : (
                groupsData.groups.map((group) => (
                  <div
                    key={group.id}
                    className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {group.title || "Sans titre"}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {group.author != null && group.author + " • "}
                          {group.publicationYear != null &&
                            group.publicationYear.toString() + " • "}
                          {group.rowCount.toString()} exemplaire(s)
                        </p>
                        <p className="mt-1 font-mono text-xs text-gray-400">
                          Notice: {group.legacyNoticeId}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {group.catalogMatch.recordId && (
                          <div className="text-right">
                            <span
                              className={
                                "text-sm font-medium " +
                                (CONFIDENCE_COLORS[group.catalogMatch.confidence ?? "none"] ?? "")
                              }
                            >
                              Match{" "}
                              {CONFIDENCE_LABELS[group.catalogMatch.confidence ?? "none"] ?? ""}
                            </span>
                            <div className="mt-2 flex gap-2">
                              <button
                                onClick={() => {
                                  void handleConfirmMatch(group.id, true);
                                }}
                                disabled={confirmMatch.isPending}
                                className="rounded bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700"
                              >
                                Confirmer
                              </button>
                              <button
                                onClick={() => {
                                  void handleConfirmMatch(group.id, false);
                                }}
                                disabled={confirmMatch.isPending}
                                className="rounded border border-red-300 px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400"
                              >
                                Rejeter
                              </button>
                            </div>
                          </div>
                        )}
                        {group.bnfEnrichment.done && group.bnfEnrichment.workArk && (
                          <span className="text-xs text-purple-600 dark:text-purple-400">
                            BnF enrichi
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color?: string }) {
  const colorClasses: Record<string, string> = {
    green: "text-green-600 dark:text-green-400",
    red: "text-red-600 dark:text-red-400",
    blue: "text-blue-600 dark:text-blue-400",
    cyan: "text-cyan-600 dark:text-cyan-400",
    purple: "text-purple-600 dark:text-purple-400",
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
      <dd
        className={
          "mt-1 text-2xl font-semibold " +
          (color ? (colorClasses[color] ?? "") : "text-gray-900 dark:text-white")
        }
      >
        {value.toLocaleString("fr-FR")}
      </dd>
    </div>
  );
}
